import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Save, Trash2, Plus, Upload } from "lucide-react";
import {
  adminLogin, adminGetOrders, adminUpdateProduct, adminUpdateSettings, getProducts, getSettings,
  adminCreateProduct, adminDeleteProduct, adminUploadImage,
} from "@/lib/api";

const field = "rounded-xl border-2 border-ink/10 bg-[#F8FAF5] px-3 py-2 outline-none focus:border-watermelon";

const EMPTY_PRODUCT = { name: "", price: "6.00", size: "12 fl oz (355 mL)", color: "pink", tagline: "With real fruit", ingredients: "", benefits: "", image: "" };

export default function Admin() {
  const [token, setToken] = useState(sessionStorage.getItem("zoju_admin") || "");
  const [password, setPassword] = useState("");
  const [products, setProducts] = useState([]);
  const [cashtag, setCashtag] = useState("");
  const [orders, setOrders] = useState([]);
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async (t) => {
    try {
      const [prods, settings, ords] = await Promise.all([getProducts(), getSettings(), adminGetOrders(t)]);
      setProducts(prods);
      setCashtag(settings.cashtag || "");
      setOrders(ords);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Couldn't refresh data. Please try again.");
      }
    }
  };

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async (e) => {
    e.preventDefault();
    try {
      const { token: t } = await adminLogin(password);
      sessionStorage.setItem("zoju_admin", t);
      setToken(t);
      await load(t);
      toast.success("Welcome back!");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => { sessionStorage.removeItem("zoju_admin"); setToken(""); setPassword(""); };

  const saveProduct = async (p) => {
    try {
      await adminUpdateProduct(p.id, { price: parseFloat(p.price), sold_out: p.sold_out }, token);
      toast.success(`${p.name} updated.`);
    } catch { toast.error("Update failed."); }
  };

  const saveCashtag = async () => {
    try { await adminUpdateSettings({ cashtag }, token); toast.success("Cashtag saved."); }
    catch { toast.error("Update failed."); }
  };

  const patch = (id, key, val) => setProducts((ps) => ps.map((p) => (p.id === id ? { ...p, [key]: val } : p)));

  const deleteProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    try {
      await adminDeleteProduct(p.id, token);
      toast.success(`${p.name} deleted.`);
      await load(token);
    } catch { toast.error("Delete failed."); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await adminUploadImage(fd, token);
      setNewProduct((n) => ({ ...n, image: res.url }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim()) return toast.error("Enter a product name.");
    if (!newProduct.price || parseFloat(newProduct.price) <= 0) return toast.error("Enter a valid price.");
    setCreating(true);
    try {
      await adminCreateProduct({
        name: newProduct.name.trim(),
        price: parseFloat(newProduct.price),
        size: newProduct.size.trim(),
        color: newProduct.color,
        tagline: newProduct.tagline.trim(),
        ingredients: newProduct.ingredients.split(",").map((s) => s.trim()).filter(Boolean),
        benefits: newProduct.benefits.split(",").map((s) => s.trim()).filter(Boolean),
        image: newProduct.image,
      }, token);
      toast.success("Product added!");
      setNewProduct(EMPTY_PRODUCT);
      await load(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add product.");
    } finally { setCreating(false); }
  };

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center bg-cream px-5">
        <form onSubmit={login} className="w-full max-w-sm rounded-[2rem] border border-white/60 bg-white/80 p-8 backdrop-blur-xl" data-testid="admin-login-form">
          <div className="grid h-14 w-14 place-items-center rounded-full bg-watermelon text-white"><Lock className="h-6 w-6" /></div>
          <h1 className="mt-5 font-display text-3xl font-extrabold uppercase tracking-tight text-ink">Admin</h1>
          <p className="mt-1 text-sm text-ink/50">ZoJu Juices dashboard</p>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" data-testid="admin-password"
            className={`mt-6 w-full ${field}`}
          />
          <button type="submit" data-testid="admin-login-btn" className="mt-4 w-full rounded-full bg-watermelon py-3 font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95">
            Log in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream px-5 sm:px-8 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-cut.png" alt="ZoJu" className="h-10 w-auto" />
            <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-ink">Admin</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border-2 border-ink/15 px-4 py-2 text-sm font-semibold text-ink hover:border-ink/40">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>

        {/* Products */}
        <div className="mt-8 rounded-[2rem] border border-white/60 bg-white/80 p-6 backdrop-blur-xl">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-ink">Products</h2>
          <div className="mt-4 space-y-4">
            {products.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-2xl bg-cream p-4" data-testid={`admin-product-${p.id}`}>
                <img src={p.image} alt={p.name} className="h-14 w-auto" />
                <span className="flex-1 font-semibold text-ink">{p.name}</span>
                <div className="flex items-center gap-1">
                  <span className="text-ink/50">$</span>
                  <input
                    type="number" step="0.01" min="0" value={p.price}
                    onChange={(e) => patch(p.id, "price", e.target.value)}
                    className={`w-24 ${field}`} data-testid={`admin-price-${p.id}`}
                  />
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-ink">
                  <input
                    type="checkbox" checked={!!p.sold_out}
                    onChange={(e) => patch(p.id, "sold_out", e.target.checked)}
                    data-testid={`admin-soldout-${p.id}`}
                  />
                  Sold out
                </label>
                <button onClick={() => saveProduct(p)} data-testid={`admin-save-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/80">
                  <Save className="h-4 w-4" /> Save
                </button>
                <button onClick={() => deleteProduct(p)} data-testid={`admin-delete-${p.id}`} className="inline-flex items-center gap-1.5 rounded-full border-2 border-destructive/30 px-3 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10" title="Delete product">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Add new product */}
        <div className="mt-6 rounded-[2rem] border border-white/60 bg-white/80 p-6 backdrop-blur-xl">
          <h2 className="flex items-center gap-2 font-display text-xl font-bold uppercase tracking-tight text-ink">
            <Plus className="h-5 w-5 text-watermelon" /> Add New Product
          </h2>
          <form onSubmit={createProduct} className="mt-4 grid gap-4 sm:grid-cols-2" data-testid="admin-add-form">
            <div>
              <label className="text-sm font-semibold text-ink">Name</label>
              <input className={`mt-1 w-full ${field}`} value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g. Mango Ginger" data-testid="new-name" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Price ($)</label>
              <input type="number" step="0.01" min="0" className={`mt-1 w-full ${field}`} value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} data-testid="new-price" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Size</label>
              <input className={`mt-1 w-full ${field}`} value={newProduct.size} onChange={(e) => setNewProduct({ ...newProduct, size: e.target.value })} data-testid="new-size" />
            </div>
            <div>
              <label className="text-sm font-semibold text-ink">Color theme</label>
              <select className={`mt-1 w-full ${field}`} value={newProduct.color} onChange={(e) => setNewProduct({ ...newProduct, color: e.target.value })} data-testid="new-color">
                <option value="pink">Watermelon Pink</option>
                <option value="blue">Ocean Blue</option>
                <option value="gold">Golden Yellow</option>
                <option value="green">Leaf Green</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-ink">Tagline</label>
              <input className={`mt-1 w-full ${field}`} value={newProduct.tagline} onChange={(e) => setNewProduct({ ...newProduct, tagline: e.target.value })} placeholder="With real fruit" data-testid="new-tagline" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-ink">Ingredients <span className="font-normal text-ink/40">(comma-separated)</span></label>
              <input className={`mt-1 w-full ${field}`} value={newProduct.ingredients} onChange={(e) => setNewProduct({ ...newProduct, ingredients: e.target.value })} placeholder="Mango, Ginger, Agave" data-testid="new-ingredients" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-ink">Benefits <span className="font-normal text-ink/40">(comma-separated)</span></label>
              <input className={`mt-1 w-full ${field}`} value={newProduct.benefits} onChange={(e) => setNewProduct({ ...newProduct, benefits: e.target.value })} placeholder="Hydration, Immune Support" data-testid="new-benefits" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-sm font-semibold text-ink">Product image</label>
              <div className="mt-1 flex items-center gap-4">
                <label className={`inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-ink/15 px-4 py-2.5 text-sm font-semibold text-ink hover:border-watermelon ${uploading ? "opacity-60" : ""}`} data-testid="new-image-label">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={uploadImage} disabled={uploading} data-testid="new-image-input" />
                </label>
                {newProduct.image && <img src={newProduct.image} alt="preview" className="h-16 w-auto rounded-lg" data-testid="new-image-preview" />}
              </div>
            </div>
            <div className="sm:col-span-2">
              <button type="submit" disabled={creating} className="inline-flex items-center gap-2 rounded-full bg-watermelon px-6 py-3 font-semibold text-white transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60" data-testid="new-submit">
                <Plus className="h-4 w-4" /> {creating ? "Adding…" : "Add product"}
              </button>
            </div>
          </form>
        </div>

        {/* Cashtag */}
        <div className="mt-6 rounded-[2rem] border border-white/60 bg-white/80 p-6 backdrop-blur-xl">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-ink">Cash App Cashtag</h2>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <input value={cashtag} onChange={(e) => setCashtag(e.target.value)} className={`flex-1 ${field}`} placeholder="$ZoJuJuices" data-testid="admin-cashtag" />
            <button onClick={saveCashtag} data-testid="admin-save-cashtag" className="inline-flex items-center gap-1.5 rounded-full bg-watermelon px-5 py-2.5 text-sm font-semibold text-white hover:scale-[1.02]">
              <Save className="h-4 w-4" /> Save
            </button>
          </div>
        </div>

        {/* Orders */}
        <div className="mt-6 rounded-[2rem] border border-white/60 bg-white/80 p-6 backdrop-blur-xl">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-ink">Recent Orders ({orders.length})</h2>
          <div className="mt-4 space-y-3">
            {orders.length === 0 && <p className="text-sm text-ink/50">No orders yet.</p>}
            {orders.map((o) => (
              <div key={o.id} className="rounded-2xl bg-cream p-4 text-sm" data-testid={`admin-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-ink">{o.name} · {o.phone}</span>
                  <span className="font-display text-lg font-bold text-watermelon">${o.total.toFixed(2)}</span>
                </div>
                <p className="mt-1 text-ink/60">
                  {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")} · {o.fulfillment} · {o.preferred_day}
                </p>
                {o.address && <p className="text-ink/50">📍 {o.address}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
